// apps/pwa-pos/src/components/CheckoutModal.tsx
// Modal de cobro del POS — maneja los métodos de pago, selección de cliente
// para fiado, cálculo de cambio, y lista de pagos mixtos que deben cuadrar el
// total. No persiste por sí mismo — devuelve los datos al padre vía `onConfirm`.

import { useEffect, useMemo, useState, useId, useRef, useCallback, type RefObject } from 'react';
import {
  Wallet,
  UserCheck,
  DollarSign,
  CreditCard,
  ArrowRightLeft,
  Plus,
  Trash2,
  CheckCircle2,
  Search,
  Printer,
  CircleAlert,
} from 'lucide-react';
import { parseMoney, roundMoney } from '@mibodega/domain';
import { formatMoney, formatMoneyInput } from '@/lib/format';
import {
  METODOS_CON_REFERENCIA,
  calcularCambio,
  esPagoMixto,
  labelMetodo,
  faltantePago,
  pagoCubreTotal,
  pagosCuadran,
  sumaPagos,
  type PagoMetodo,
  type PagoVenta,
} from '@/lib/payments';
import { type Customer, clienteAdmiteFiar, creditoDisponible } from '@/lib/customers';
import { Dialog } from './Dialog';
import { Button } from './Button';
import { TextField } from './TextField';

interface PagoVentaUI extends PagoVenta {
  rawMonto?: string;
}

export interface CheckoutConfirm {
  pagos: PagoVenta[];
  clienteId: string | null;
  esCredito: boolean;
  imprimir: boolean;
}

interface CheckoutModalProps {
  isOpen: boolean;
  total: number;
  monedaCodigo: string;
  monedaBase: string;
  tasaUSD?: number | null;
  customers: Customer[];
  defaultPrint: boolean;
  onClose: () => void;
  onConfirm: (data: CheckoutConfirm) => void;
}

type MetodoUI = PagoMetodo | 'MIXTO';

const METODOS: MetodoUI[] = ['EFECTIVO', 'CREDITO', 'DOLARES', 'TARJETA', 'TRANSFERENCIA', 'MIXTO'];

export function CheckoutModal({
  isOpen,
  total,
  monedaCodigo,
  monedaBase,
  tasaUSD,
  customers,
  defaultPrint,
  onClose,
  onConfirm,
}: CheckoutModalProps) {
  const titleId = useId();
  const descId = useId();

  const [metodo, setMetodo] = useState<MetodoUI>('EFECTIVO');

  // Estado por método
  const [pagoCon, setPagoCon] = useState('');
  const [dolaresMonto, setDolaresMonto] = useState('');
  const [referencia, setReferencia] = useState('');
  const [clienteQuery, setClienteQuery] = useState('');
  const [clienteId, setClienteId] = useState<string | null>(null);

  // Estado mixto
  const [mixtoPagos, setMixtoPagos] = useState<PagoVentaUI[]>([]);

  // Decisión de impresión al cierre
  const [imprimir, setImprimir] = useState(defaultPrint);

  // Estado de éxito post-cobro
  const [success, setSuccess] = useState<{ consecutivo: string; cambio: number } | null>(null);
  const pagoConInputRef = useRef<HTMLInputElement | null>(null);
  const dolaresInputRef = useRef<HTMLInputElement | null>(null);
  const referenciaInputRef = useRef<HTMLInputElement | null>(null);
  const clienteInputRef = useRef<HTMLInputElement | null>(null);

  // Stable close handler so Dialog's lifecycle effect does not re-fire.
  const handleDialogClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // Focus target for the active method when the modal opens / method changes.
  const activeInputRef =
    metodo === 'EFECTIVO'
      ? pagoConInputRef
      : metodo === 'DOLARES'
        ? dolaresInputRef
        : metodo === 'CREDITO'
          ? clienteInputRef
          : metodo === 'TARJETA' || metodo === 'TRANSFERENCIA'
            ? referenciaInputRef
            : undefined;

  // Reset al abrir / cambiar método
  useEffect(() => {
    if (!isOpen) {
      setMetodo('EFECTIVO');
      setPagoCon('');
      setDolaresMonto('');
      setReferencia('');
      setClienteQuery('');
      setClienteId(null);
      setMixtoPagos([]);
      setImprimir(defaultPrint);
      setSuccess(null);
    }
  }, [isOpen, defaultPrint]);

  // After open or method change, land the cashier on the money/search field.
  useEffect(() => {
    if (!isOpen || success) return;
    const id = window.requestAnimationFrame(() => {
      activeInputRef?.current?.focus();
      activeInputRef?.current?.select?.();
    });
    return () => window.cancelAnimationFrame(id);
  }, [isOpen, metodo, success]);

  // MIB-47 — Enter como atajo primario de Cobrar.
  // Wrapper en <form onSubmit> (más abajo) hace que Enter dispare
  // handleCobrar de forma nativa sin chocar con F12 (que en el navegador
  // normal lo captura DevTools). Eliminado el handler manual de F12.

  const selectedCustomer = useMemo(
    () => customers.find((c) => c.id === clienteId) ?? null,
    [customers, clienteId],
  );

  const filteredCustomers = useMemo(() => {
    const q = clienteQuery.trim().toLowerCase();
    if (!q) return customers.slice(0, 50);
    return customers
      .filter(
        (c) =>
          c.nombre.toLowerCase().includes(q) ||
          (c.codigo && c.codigo.toLowerCase().includes(q)) ||
          (c.numero_documento && c.numero_documento.toLowerCase().includes(q)),
      )
      .slice(0, 50);
  }, [customers, clienteQuery]);

  // ============================== Derivados por método ==============================

  const cambioEfectivo = useMemo(() => {
    const n = parseMoney(pagoCon);
    return Number.isFinite(n) ? calcularCambio(n, total) : 0;
  }, [pagoCon, total]);

  const dolaresNum = useMemo(() => parseMoney(dolaresMonto), [dolaresMonto]);
  const dolaresEnBase = useMemo(() => {
    if (!Number.isFinite(dolaresNum)) return null;
    if (!tasaUSD) return null;
    return roundMoney(dolaresNum * tasaUSD);
  }, [dolaresNum, tasaUSD]);

  const creditoOk = useMemo(() => {
    if (!selectedCustomer) return { ok: false, msg: '' };
    if (selectedCustomer.limite_credito <= 0) {
      return { ok: false, msg: `${selectedCustomer.nombre} no tiene crédito habilitado` };
    }
    if (!clienteAdmiteFiar(selectedCustomer, total)) {
      return {
        ok: false,
        msg: `Excede el límite. Crédito disponible: ${formatMoney(
          creditoDisponible(selectedCustomer),
          monedaBase,
        )}`,
      };
    }
    return { ok: true, msg: '' };
  }, [selectedCustomer, total, monedaBase]);

  // ============================== Pago resultante por método ==============================

  const pagosActuales = useMemo<PagoVenta[]>(() => {
    if (metodo === 'EFECTIVO') {
      const n = parseMoney(pagoCon);
      if (!Number.isFinite(n) || n <= 0) return [];
      return [{ metodo: 'EFECTIVO', monedaCodigo, monto: n }];
    }
    if (metodo === 'CREDITO') {
      if (!selectedCustomer) return [];
      return [{ metodo: 'CREDITO', monedaCodigo, monto: total }];
    }
    if (metodo === 'DOLARES') {
      if (!Number.isFinite(dolaresNum) || dolaresNum <= 0) return [];
      return [
        {
          metodo: 'DOLARES',
          monedaCodigo: 'USD',
          monto: dolaresNum,
          tasaCambio: tasaUSD ?? undefined,
          montoBase: dolaresEnBase ?? undefined,
        },
      ];
    }
    if (metodo === 'TARJETA' || metodo === 'TRANSFERENCIA') {
      return [{ metodo, monedaCodigo, monto: total, referencia: referencia || undefined }];
    }
    return mixtoPagos;
  }, [
    metodo,
    pagoCon,
    dolaresNum,
    dolaresEnBase,
    selectedCustomer,
    referencia,
    mixtoPagos,
    monedaCodigo,
    total,
    tasaUSD,
  ]);

  const puedeCobrar = useMemo(() => {
    if (metodo === 'CREDITO') return creditoOk.ok;
    if (metodo === 'MIXTO') return pagosCuadran(pagosActuales, total);
    if (metodo === 'DOLARES') {
      // Dólares se valida en moneda base convertida; sin tasa no se puede cobrar.
      if (dolaresEnBase == null) return false;
      return pagoCubreTotal(dolaresEnBase, total);
    }
    if (metodo === 'EFECTIVO') {
      const n = parseMoney(pagoCon);
      return pagoCubreTotal(n, total);
    }
    // TARJETA / TRANSFERENCIA: el monto se fija al total de la venta.
    return pagosActuales.length > 0 && pagoCubreTotal(pagosActuales[0].monto, total);
  }, [metodo, creditoOk, pagosActuales, total, dolaresEnBase, pagoCon]);

  // ============================== Acciones ==============================

  function handleMetodoChange(m: MetodoUI) {
    setMetodo(m);
    if (m !== 'EFECTIVO') setPagoCon('');
    if (m !== 'DOLARES') setDolaresMonto('');
    if (m !== 'TARJETA' && m !== 'TRANSFERENCIA') setReferencia('');
    if (m !== 'CREDITO') setClienteId(null);
    if (m !== 'MIXTO') setMixtoPagos([]);
  }

  function handleExacto() {
    setPagoCon(formatMoneyInput(String(roundMoney(total))));
  }

  function addMixtoPago() {
    setMixtoPagos((prev) => [
      ...prev,
      {
        metodo: 'EFECTIVO',
        monedaCodigo,
        monto: 0,
        rawMonto: '',
      },
    ]);
  }

  function updateMixtoPago(idx: number, patch: Partial<PagoVentaUI>) {
    setMixtoPagos((prev) => prev.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  }

  function removeMixtoPago(idx: number) {
    setMixtoPagos((prev) => prev.filter((_, i) => i !== idx));
  }

  function handleCobrar() {
    if (!puedeCobrar || success) return;
    // Defensa en profundidad: nunca confirmar un contado por debajo del total.
    if (metodo === 'EFECTIVO') {
      const n = parseMoney(pagoCon);
      if (!pagoCubreTotal(n, total)) return;
    }
    if (metodo === 'DOLARES' && (dolaresEnBase == null || !pagoCubreTotal(dolaresEnBase, total))) {
      return;
    }
    if (metodo === 'MIXTO' && !pagosCuadran(pagosActuales, total)) return;
    onConfirm({
      pagos: pagosActuales,
      clienteId: metodo === 'CREDITO' ? clienteId : null,
      esCredito: metodo === 'CREDITO',
      imprimir,
    });
    setSuccess({ consecutivo: '', cambio: cambioEfectivo });
  }

  const handleSuccessClose = useCallback(() => {
    setSuccess(null);
    onClose();
  }, [onClose]);

  // Render =====================================================================

  return (
    <Dialog
      open={isOpen}
      onClose={success ? handleSuccessClose : handleDialogClose}
      initialFocusRef={activeInputRef as RefObject<HTMLElement | null> | undefined}
      title={
        <span className="flex items-center gap-2">
          <CheckCircle2 className="text-primary h-5 w-5" aria-hidden="true" />
          Cobrar
        </span>
      }
      titleId={titleId}
      descriptionId={descId}
      description="Elegí el método de pago y confirmá la venta."
      size="lg"
    >
      {success ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 py-8 text-center">
          <CheckCircle2 className="text-success h-16 w-16" aria-hidden="true" />
          <p className="text-lg font-semibold text-neutral-900">¡Venta cobrada!</p>
          {success.cambio > 0 && (
            <p className="text-sm text-neutral-600">
              Cambio a devolver:{' '}
              <span className="tnum text-success font-mono font-bold">
                {formatMoney(success.cambio, monedaCodigo)}
              </span>
            </p>
          )}
          <Button onClick={handleSuccessClose} variant="primary" className="mt-4">
            Aceptar
          </Button>
        </div>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleCobrar();
          }}
          className="flex flex-1 flex-col"
        >
          {/* Total */}
          <div className="bg-primary-soft -mx-6 -mt-4 mb-4 border-b border-neutral-200 px-6 py-5 text-center">
            <p className="text-label text-primary font-medium tracking-wider uppercase">
              Total a cobrar
            </p>
            <p className="tnum text-display text-primary mt-1 font-mono font-bold">
              {formatMoney(total, monedaCodigo)}
            </p>
          </div>

          {/* Grid de métodos */}
          <div className="mb-4">
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
              {METODOS.map((m) => {
                const Icon = iconForMethod(m);
                const active = metodo === m;
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={() => handleMetodoChange(m)}
                    className={
                      'flex h-16 flex-col items-center justify-center gap-1 rounded-md border-2 px-3 text-xs font-medium transition ' +
                      (active
                        ? 'border-primary bg-primary-soft text-primary'
                        : 'bg-neutral-0 border-neutral-200 text-neutral-700 hover:border-neutral-300')
                    }
                  >
                    <Icon className="h-6 w-6" aria-hidden="true" />
                    <span>{m === 'MIXTO' ? 'Mixto' : labelMetodo(m)}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Sección específica del método */}
          <div className="min-h-0 flex-1">
            {metodo === 'EFECTIVO' && (
              <EfectivoSection
                pagoCon={pagoCon}
                setPagoCon={setPagoCon}
                cambio={cambioEfectivo}
                onExacto={handleExacto}
                total={total}
                monedaCodigo={monedaCodigo}
                inputRef={pagoConInputRef}
              />
            )}
            {metodo === 'CREDITO' && (
              <CreditoSection
                customers={filteredCustomers}
                selected={selectedCustomer}
                clienteQuery={clienteQuery}
                setClienteQuery={setClienteQuery}
                setClienteId={setClienteId}
                total={total}
                monedaBase={monedaBase}
                creditoOk={creditoOk}
                inputRef={clienteInputRef}
              />
            )}
            {metodo === 'DOLARES' && (
              <DolaresSection
                dolaresMonto={dolaresMonto}
                setDolaresMonto={setDolaresMonto}
                dolaresNum={dolaresNum}
                dolaresEnBase={dolaresEnBase}
                tasaUSD={tasaUSD}
                monedaBase={monedaBase}
                totalEnBase={total}
                monedaCodigo={monedaCodigo}
                inputRef={dolaresInputRef}
              />
            )}
            {(metodo === 'TARJETA' || metodo === 'TRANSFERENCIA') && (
              <ReferenciaSection
                metodo={metodo}
                referencia={referencia}
                setReferencia={setReferencia}
                total={total}
                monedaCodigo={monedaCodigo}
                inputRef={referenciaInputRef}
              />
            )}
            {metodo === 'MIXTO' && (
              <MixtoSection
                pagos={mixtoPagos}
                update={updateMixtoPago}
                remove={removeMixtoPago}
                add={addMixtoPago}
                total={total}
                suma={sumaPagos(mixtoPagos)}
                cuadra={pagosCuadran(mixtoPagos, total)}
                monedaCodigo={monedaCodigo}
              />
            )}
          </div>

          {/* Footer del modal: acciones */}
          <div className="-mx-6 mt-6 -mb-4 space-y-3 border-t border-neutral-200 bg-neutral-50 px-6 py-4">
            {esPagoMixto(pagosActuales) && (
              <p className="bg-neutral-0 flex items-center justify-between rounded-md border border-neutral-200 px-3 py-2 text-xs">
                <span className="text-neutral-600">
                  {pagosActuales.length} pagos · Suma:{' '}
                  <span className="tnum font-mono font-semibold">
                    {formatMoney(sumaPagos(pagosActuales), monedaCodigo)}
                  </span>
                </span>
                <span
                  className={
                    pagosCuadran(pagosActuales, total)
                      ? 'text-success font-semibold'
                      : 'text-danger font-semibold'
                  }
                >
                  {pagosCuadran(pagosActuales, total) ? 'Cuadra ✓' : 'No cuadra'}
                </span>
              </p>
            )}
            <div className="flex items-center justify-between gap-3">
              <label className="flex cursor-pointer items-center gap-2 text-xs text-neutral-700">
                <input
                  type="checkbox"
                  checked={imprimir}
                  onChange={(e) => setImprimir(e.target.checked)}
                  className="text-primary rounded-sm border-neutral-300 focus:ring-[var(--color-focus-ring)]"
                />
                <Printer className="h-3.5 w-3.5" />
                Imprimir ticket
              </label>
              <div className="flex gap-2">
                <Button variant="secondary" onClick={onClose} type="button">
                  Cancelar
                  <span className="kbd-inline">
                    <kbd>Esc</kbd>
                  </span>
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={!puedeCobrar}
                  leadingIcon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
                >
                  {imprimir ? 'Cobrar e Imprimir' : 'Cobrar'}
                  <span className="kbd-inline">
                    <kbd>Enter</kbd>
                  </span>
                </Button>
              </div>

              {/* Footer de atajos: Enter dispara el submit del form. MIB-47. */}
              <div
                className="text-label mt-3 flex items-center justify-center gap-3 text-neutral-500"
                aria-label="Atajos de teclado disponibles en este diálogo"
              >
                <span className="flex items-center gap-1">
                  <kbd>Enter</kbd>
                  <span>cobrar</span>
                </span>
                <span className="text-neutral-300" aria-hidden="true">
                  ·
                </span>
                <span className="flex items-center gap-1">
                  <kbd>Esc</kbd>
                  <span>cancelar</span>
                </span>
              </div>
            </div>
          </div>
        </form>
      )}
    </Dialog>
  );
}

// ============================================================
// Sub-secciones
// ============================================================

function iconForMethod(m: MetodoUI) {
  switch (m) {
    case 'EFECTIVO':
      return Wallet;
    case 'CREDITO':
      return UserCheck;
    case 'DOLARES':
      return DollarSign;
    case 'TARJETA':
      return CreditCard;
    case 'TRANSFERENCIA':
      return ArrowRightLeft;
    case 'MIXTO':
      return ArrowRightLeft;
  }
}

function EfectivoSection({
  pagoCon,
  setPagoCon,
  cambio,
  onExacto,
  total,
  monedaCodigo,
  inputRef,
}: {
  pagoCon: string;
  setPagoCon: (s: string) => void;
  cambio: number;
  onExacto: () => void;
  total: number;
  monedaCodigo: string;
  inputRef?: RefObject<HTMLInputElement | null>;
}) {
  const pagoConNum = parseMoney(pagoCon);
  const alcanza = pagoCubreTotal(pagoConNum, total);
  const faltante = faltantePago(Number.isFinite(pagoConNum) ? pagoConNum : 0, total);

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor="cm-pago-con" className="mb-1 block text-xs font-medium text-neutral-700">
          Pagó con
        </label>
        <div className="flex gap-2">
          <input
            ref={inputRef}
            id="cm-pago-con"
            type="text"
            inputMode="decimal"
            placeholder="0,00"
            value={pagoCon}
            onChange={(e) => setPagoCon(formatMoneyInput(e.target.value))}
            aria-invalid={!alcanza && Number.isFinite(pagoConNum) && pagoConNum > 0}
            className="tnum bg-neutral-0 h-11 flex-1 rounded-md border border-neutral-300 px-3 font-mono text-lg text-neutral-900 transition-colors placeholder:text-neutral-400 hover:border-neutral-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)]"
          />
          <Button variant="secondary" onClick={onExacto}>
            Exacto
          </Button>
        </div>
      </div>
      <div
        className={
          'rounded-md border-2 px-4 py-3 ' +
          (cambio > 0
            ? 'border-success bg-success-soft'
            : alcanza
              ? 'border-neutral-200 bg-neutral-50'
              : 'border-danger bg-danger-soft')
        }
      >
        <p className="text-xs font-medium tracking-wider text-neutral-600 uppercase">
          {alcanza ? 'Cambio' : 'Falta'}
        </p>
        <p
          className={
            'tnum font-mono text-3xl font-bold ' +
            (cambio > 0 ? 'text-success' : alcanza ? 'text-neutral-700' : 'text-danger')
          }
        >
          {formatMoney(alcanza ? cambio : faltante, monedaCodigo)}
        </p>
        {!alcanza && Number.isFinite(pagoConNum) && pagoConNum > 0 ? (
          <p className="text-danger mt-1 text-xs">
            El monto no cubre el total. No se puede cobrar con faltante.
          </p>
        ) : null}
      </div>
    </div>
  );
}

function CreditoSection({
  customers,
  selected,
  clienteQuery,
  setClienteQuery,
  setClienteId,
  total,
  monedaBase,
  creditoOk,
  inputRef,
}: {
  customers: Customer[];
  selected: Customer | null;
  clienteQuery: string;
  setClienteQuery: (s: string) => void;
  setClienteId: (s: string | null) => void;
  total: number;
  monedaBase: string;
  creditoOk: { ok: boolean; msg: string };
  inputRef?: RefObject<HTMLInputElement | null>;
}) {
  return (
    <div className="space-y-3">
      <div className="relative">
        <Search
          className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-neutral-400"
          aria-hidden="true"
        />
        <input
          ref={inputRef}
          type="text"
          placeholder="Buscar cliente por nombre, código o documento…"
          value={clienteQuery}
          onChange={(e) => setClienteQuery(e.target.value)}
          className="bg-neutral-0 h-11 w-full rounded-md border border-neutral-300 py-2 pr-4 pl-9 text-sm text-neutral-900 placeholder:text-neutral-400 hover:border-neutral-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)]"
        />
      </div>

      {selected ? (
        <div className="border-primary bg-primary-soft rounded-md border-2 p-3">
          <div className="flex items-start justify-between">
            <div>
              <p className="font-semibold text-neutral-900">{selected.nombre}</p>
              <p className="text-xs text-neutral-500">
                {selected.codigo ? `Cód: ${selected.codigo} · ` : ''}
                {selected.numero_documento ? `Doc: ${selected.numero_documento}` : 'Sin documento'}
              </p>
            </div>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => setClienteId(null)}
              aria-label="Cambiar cliente"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </Button>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
            <div className="bg-neutral-0 rounded p-2">
              <p className="text-neutral-500">Saldo actual</p>
              <p className="tnum font-mono font-semibold text-neutral-900">
                {formatMoney(selected.saldo_actual, monedaBase)}
              </p>
            </div>
            <div className="bg-neutral-0 rounded p-2">
              <p className="text-neutral-500">Disponible</p>
              <p className="tnum text-success font-mono font-semibold">
                {formatMoney(creditoDisponible(selected), monedaBase)}
              </p>
            </div>
            <div className="bg-neutral-0 rounded p-2">
              <p className="text-neutral-500">Límite</p>
              <p className="tnum font-mono font-semibold text-neutral-900">
                {formatMoney(selected.limite_credito, monedaBase)}
              </p>
            </div>
          </div>
          {!creditoOk.ok && creditoOk.msg && (
            <p className="text-danger mt-2 flex items-center gap-1 text-xs">
              <CircleAlert className="h-3.5 w-3.5" /> {creditoOk.msg}
            </p>
          )}
        </div>
      ) : (
        <div className="bg-neutral-0 max-h-56 space-y-1 overflow-y-auto rounded-md border border-neutral-200">
          {customers.length === 0 ? (
            <p className="px-4 py-6 text-center text-xs text-neutral-500">
              No hay clientes cacheados. Conectate a internet una vez para descargar.
            </p>
          ) : (
            customers.map((c) => (
              <button
                key={c.id}
                onClick={() => setClienteId(c.id)}
                className="flex w-full items-center justify-between border-b border-neutral-100 px-3 py-2 text-left text-xs last:border-b-0 hover:bg-neutral-50"
              >
                <div>
                  <p className="font-medium text-neutral-900">{c.nombre}</p>
                  <p className="text-[10px] text-neutral-500">
                    {c.codigo ?? '—'} · Saldo: {formatMoney(c.saldo_actual, monedaBase)} /{' '}
                    {formatMoney(c.limite_credito, monedaBase)}
                  </p>
                </div>
                <UserCheck className="h-4 w-4 text-neutral-400" />
              </button>
            ))
          )}
        </div>
      )}

      <p className="text-xs text-neutral-500">
        Esta venta generará un cargo por{' '}
        <span className="tnum font-mono font-semibold">{formatMoney(total, monedaBase)}</span> en la
        cuenta del cliente.
      </p>
    </div>
  );
}

function DolaresSection({
  dolaresMonto,
  setDolaresMonto,
  dolaresNum,
  dolaresEnBase,
  tasaUSD,
  monedaBase,
  totalEnBase,
  monedaCodigo,
  inputRef,
}: {
  dolaresMonto: string;
  setDolaresMonto: (s: string) => void;
  dolaresNum: number;
  dolaresEnBase: number | null;
  tasaUSD: number | null | undefined;
  monedaBase: string;
  totalEnBase: number;
  monedaCodigo: string;
  inputRef?: RefObject<HTMLInputElement | null>;
}) {
  const cuadra = dolaresEnBase !== null && Math.abs(dolaresEnBase - totalEnBase) < 0.01;

  return (
    <div className="space-y-4">
      <TextField
        ref={inputRef}
        id="cm-usd"
        label="Monto en USD"
        type="text"
        inputMode="decimal"
        placeholder="0,00"
        value={dolaresMonto}
        onChange={(e) => setDolaresMonto(formatMoneyInput(e.target.value))}
        className="tnum font-mono text-lg"
      />
      <div className="rounded-md border border-neutral-200 bg-neutral-50 p-3 text-xs">
        <div className="flex justify-between">
          <span className="text-neutral-600">Tasa USD → {monedaBase}</span>
          <span className="tnum font-mono font-semibold">{tasaUSD ? tasaUSD.toFixed(2) : '—'}</span>
        </div>
        <div className="mt-2 flex justify-between border-t border-neutral-200 pt-2">
          <span className="text-neutral-600">Conversión</span>
          <span
            className={'tnum font-mono font-semibold ' + (cuadra ? 'text-success' : 'text-danger')}
          >
            {dolaresEnBase !== null ? formatMoney(dolaresEnBase, monedaBase) : '—'}
          </span>
        </div>
        <p className="mt-2 text-[11px] text-neutral-500">
          Total a cobrar: {formatMoney(totalEnBase, monedaBase)} ({monedaCodigo})
        </p>
      </div>
      {!cuadra && Number.isFinite(dolaresNum) && dolaresNum > 0 && (
        <p className="text-danger flex items-center gap-1 text-xs">
          <CircleAlert className="h-3.5 w-3.5" /> El monto en USD no cuadra con el total
        </p>
      )}
    </div>
  );
}

function ReferenciaSection({
  metodo,
  referencia,
  setReferencia,
  total,
  monedaCodigo,
  inputRef,
}: {
  metodo: 'TARJETA' | 'TRANSFERENCIA';
  referencia: string;
  setReferencia: (s: string) => void;
  total: number;
  monedaCodigo: string;
  inputRef?: RefObject<HTMLInputElement | null>;
}) {
  return (
    <div className="space-y-3">
      <p className="text-sm text-neutral-700">
        Vas a cobrar{' '}
        <span className="tnum text-primary font-mono font-bold">
          {formatMoney(total, monedaCodigo)}
        </span>{' '}
        por {metodo === 'TARJETA' ? 'datáfono' : 'transferencia bancaria'}.
      </p>
      {METODOS_CON_REFERENCIA.includes(metodo) && (
        <TextField
          ref={inputRef}
          id="cm-ref"
          label="Nro. de aprobación / referencia (opcional)"
          type="text"
          placeholder={metodo === 'TARJETA' ? '123456' : 'OP-12345'}
          value={referencia}
          onChange={(e) => setReferencia(e.target.value)}
          className="tnum font-mono"
        />
      )}
    </div>
  );
}

function MixtoSection({
  pagos,
  update,
  remove,
  add,
  total,
  suma,
  cuadra,
  monedaCodigo,
}: {
  pagos: PagoVentaUI[];
  update: (idx: number, patch: Partial<PagoVentaUI>) => void;
  remove: (idx: number) => void;
  add: () => void;
  total: number;
  suma: number;
  cuadra: boolean;
  monedaCodigo: string;
}) {
  return (
    <div className="space-y-3">
      <p className="text-xs text-neutral-600">
        Agregá 2 o más pagos parciales. La suma debe ser exactamente{' '}
        <span className="tnum font-mono font-semibold">{formatMoney(total, monedaCodigo)}</span>{' '}
        para habilitar Cobrar.
      </p>

      <div className="space-y-2">
        {pagos.map((p, idx) => (
          <div
            key={idx}
            className="bg-neutral-0 flex items-center gap-2 rounded-md border border-neutral-200 p-2"
          >
            <select
              value={p.metodo}
              onChange={(e) => update(idx, { metodo: e.target.value as PagoMetodo })}
              className="bg-neutral-0 h-9 rounded-sm border border-neutral-300 px-2 text-xs text-neutral-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)]"
              aria-label={`Método pago ${idx + 1}`}
            >
              {(['EFECTIVO', 'TARJETA', 'TRANSFERENCIA', 'DOLARES'] as PagoMetodo[]).map((m) => (
                <option key={m} value={m}>
                  {labelMetodo(m)}
                </option>
              ))}
            </select>
            <input
              type="text"
              inputMode="decimal"
              placeholder="0,00"
              value={p.rawMonto ?? (p.monto === 0 ? '' : String(p.monto))}
              onChange={(e) => {
                const formatted = formatMoneyInput(e.target.value);
                const n = parseMoney(formatted);
                update(idx, {
                  monto: Number.isFinite(n) ? n : 0,
                  rawMonto: formatted,
                });
              }}
              className="tnum bg-neutral-0 h-9 w-24 rounded-sm border border-neutral-300 px-2 text-right font-mono text-xs text-neutral-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)]"
              aria-label={`Monto pago ${idx + 1}`}
            />
            {METODOS_CON_REFERENCIA.includes(p.metodo) && (
              <input
                type="text"
                placeholder="Ref."
                value={p.referencia ?? ''}
                onChange={(e) => update(idx, { referencia: e.target.value })}
                className="tnum bg-neutral-0 h-9 w-20 rounded-sm border border-neutral-300 px-2 font-mono text-xs text-neutral-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)]"
                aria-label={`Referencia pago ${idx + 1}`}
              />
            )}
            <Button
              size="icon"
              variant="ghost"
              onClick={() => remove(idx)}
              aria-label={`Quitar pago ${idx + 1}`}
              className="text-danger hover:bg-danger-soft"
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        ))}
      </div>

      <Button variant="secondary" fullWidth onClick={add}>
        <Plus className="h-3.5 w-3.5" aria-hidden="true" /> Agregar pago
      </Button>

      <div
        className={
          'rounded-md border-2 px-3 py-2 text-sm ' +
          (cuadra ? 'border-success bg-success-soft' : 'border-warning bg-warning-soft')
        }
      >
        <div className="flex justify-between">
          <span className="font-medium text-neutral-700">Suma de pagos:</span>
          <span className="tnum font-mono font-bold">{formatMoney(suma, monedaCodigo)}</span>
        </div>
        <div className="mt-1 flex justify-between text-xs text-neutral-600">
          <span>Total:</span>
          <span className="tnum font-mono">{formatMoney(total, monedaCodigo)}</span>
        </div>
        <div className="mt-1 flex justify-between text-xs">
          <span className="text-neutral-600">Diferencia:</span>
          <span
            className={'tnum font-mono font-semibold ' + (cuadra ? 'text-success' : 'text-danger')}
          >
            {formatMoney(Math.abs(roundMoney(total - suma)), monedaCodigo)}
          </span>
        </div>
      </div>
    </div>
  );
}
